import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import {
  Building2,
  ArrowLeft,
  CalendarDays,
  Eye,
  ArrowRight,
  BookOpen
} from 'lucide-react';

import ThemeToggle from '@/components/ThemeToggle';

export default function PostsPage() {
  const { branding: brandData } = useBranding();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const res = await api.get('/posts');
        setPosts(res.data.posts || []);
      } catch (_) { toast.error('Error al cargar datos'); } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              {brandData?.logo_url ? (
                <img src={resolveImageUrl(brandData.logo_url)} alt="" className="w-10 h-10 rounded-lg object-contain" />
              ) : (
                <div className="w-10 h-10 bg-[#264639] rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="font-bold text-xl text-[#1C1917]" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                {brandData?.site_name || 'Estadía 365'}
              </span>
            </Link>
            <Link to="/properties">
              <Button variant="outline" className="border-[#264639] text-[#264639]">
                Ver alojamientos
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="text-sm text-[#57534E] hover:text-[#264639] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>

          <div className="mb-10">
            {/* Cover banner with image background */}
            <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg h-40 sm:h-56" data-testid="posts-cover-banner">
              <img src={brandData?.instagram_cover_url || "https://static.prod-images.emergentagent.com/jobs/36ba2330-e98e-4f0e-86ae-0da10db6dd0d/images/12ce9436c6945c44d965c93cafb4170f7766088cc93098faac9f3d96ae848357.png"} alt="Estadía 365" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5 sm:p-6">
                <h1 className="text-2xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading, Outfit)' }} data-testid="posts-page-title">
                  Publicaciónes
                </h1>
                <p className="text-white/80 text-sm mt-1">Novedades, consejos y destinos recomendados</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl overflow-hidden">
                  <div className="h-48 skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 skeleton rounded w-1/3" />
                    <div className="h-5 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <BookOpen className="w-14 h-14 text-[#78716C] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1C1917] mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                Próximamente
              </h3>
              <p className="text-[#57534E]">Estamos preparando contenido interesante para vos</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => {
                const isEvent = post.category?.toLowerCase() === 'eventos' || post.category?.toLowerCase() === 'evento';
                return (
                  <article key={post.post_id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group h-full flex flex-col" data-testid={`post-card-${post.post_id}`}>
                    <Link to={`/publicaciónes/${post.post_id}`} className="block">
                      {post.image_url ? (
                        <div className="aspect-[4/3] overflow-hidden">
                          <img src={post.image_url} alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-gradient-to-br from-[#264639] to-[#3D6B57] flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-white/50" />
                        </div>
                      )}
                    </Link>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-xs text-[#78716C] mb-3">
                        {post.category && (
                          <span className={`px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wider ${isEvent ? 'bg-[#C36F4D]/10 text-[#C36F4D]' : 'bg-[#DCE9E2] text-[#264639]'}`}>
                            {post.category}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" /> {formatDate(post.created_at)}
                        </span>
                        {post.views > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {post.views}
                          </span>
                        )}
                      </div>
                      <Link to={`/publicaciónes/${post.post_id}`}>
                        <h2 className="text-lg font-semibold text-[#1C1917] mb-2 line-clamp-2 group-hover:text-[#264639] transition-colors" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                          {post.title}
                        </h2>
                      </Link>
                      {post.excerpt && (
                        <p className="text-sm text-[#57534E] line-clamp-3 mb-4">{post.excerpt}</p>
                      )}
                      <div className="mt-auto flex items-center gap-2">
                        <Link to={`/publicaciónes/${post.post_id}`}
                          className="inline-flex items-center text-sm font-medium" style={{ color: 'var(--brand-primary, #264639)' }}>
                          Leer mas <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Link>
                        {isEvent && (
                          <Link to="/properties?category=eventos"
                            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C36F4D] text-white text-xs font-semibold hover:bg-[#A85A3A] transition-colors"
                            data-testid={`event-btn-${post.post_id}`}>
                            Ver Propiedades
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
